import { Search, UserPlus } from "lucide-react";
import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  type Contact,
  useAddProjectContact,
  useContacts,
  useProjectContacts,
} from "@/hooks/use-contacts";

interface Props {
  projectId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AddExistingContactDialog({ projectId, open, onOpenChange }: Props) {
  const { data: allContacts } = useContacts();
  const { data: projectContacts } = useProjectContacts(projectId);
  const addProjectContact = useAddProjectContact(projectId);
  const [search, setSearch] = useState("");

  const assignedIds = useMemo(
    () => new Set((projectContacts ?? []).map((c) => c.id)),
    [projectContacts],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (allContacts ?? [])
      .filter((c) => c.companyId)
      .filter((c) => !assignedIds.has(c.id))
      .filter((c) => {
        if (!q) return true;
        const haystack = [c.firstName, c.lastName, c.email, c.title, c.companyName]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return haystack.includes(q);
      });
  }, [allContacts, assignedIds, search]);

  function handleAdd(c: Contact) {
    addProjectContact.mutate(c.id, {
      onSuccess: () => onOpenChange(false),
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold text-[#334155]">
            Add Existing Contact
          </DialogTitle>
        </DialogHeader>
        <div className="relative">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search by name, email, title, or company…"
            className="pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="max-h-72 overflow-y-auto space-y-1">
          {filtered.length === 0 ? (
            <p className="py-6 text-center text-sm text-gray-500">
              {(allContacts ?? []).length === 0
                ? "No contacts in the directory yet."
                : "No matching contacts. Contacts without a company are excluded."}
            </p>
          ) : (
            filtered.map((c) => {
              const name = [c.firstName, c.lastName].filter(Boolean).join(" ") || "Unnamed";
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => handleAdd(c)}
                  disabled={addProjectContact.isPending}
                  className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm hover:bg-gray-50 transition-colors text-left disabled:opacity-50"
                >
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#415A77]/10">
                    <UserPlus className="h-4 w-4 text-[#415A77]" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-gray-900 truncate">{name}</p>
                    <p className="text-xs text-gray-500 truncate">
                      {[c.title, c.companyName, c.email].filter(Boolean).join(" · ") || "—"}
                    </p>
                  </div>
                </button>
              );
            })
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
